import { ref, computed, watch, readonly, getCurrentScope, onScopeDispose } from "vue";
import { useWebSocket as useVueUseWebSocket } from "@vueuse/core";
const RECONNECT_INITIAL_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 5000;
const CONNECT_TIMEOUT_MS = 20_000;
const DEAD_CONNECTION_TIMEOUT_MS = 45_000;
export const useResilientWebSocket = (options) => {
    const { onMessage, onOpen, onClose, connectTimeoutMs = CONNECT_TIMEOUT_MS, deadConnectionTimeoutMs = DEAD_CONNECTION_TIMEOUT_MS } = options;
    const activeUrl = ref(undefined);
    const disconnectedSeconds = ref(0);
    let connectTimeoutTimer = null;
    let deadConnectionTimer = null;
    let disconnectedTimer = null;
    let disconnectedAt = null;
    let explicitlyDisconnected = false;
    const clearConnectTimeout = () => {
        if (connectTimeoutTimer !== null) {
            clearTimeout(connectTimeoutTimer);
            connectTimeoutTimer = null;
        }
    };
    const clearDeadConnectionTimer = () => {
        if (deadConnectionTimer !== null) {
            clearTimeout(deadConnectionTimer);
            deadConnectionTimer = null;
        }
    };
    const clearDisconnectedTimer = () => {
        if (disconnectedTimer !== null) {
            clearInterval(disconnectedTimer);
            disconnectedTimer = null;
        }
    };
    const resetDeadConnectionTimer = () => {
        clearDeadConnectionTimer();
        deadConnectionTimer = setTimeout(() => {
            deadConnectionTimer = null;
            ws.value?.close();
        }, deadConnectionTimeoutMs);
    };
    // Registered before useVueUseWebSocket so this cleanup runs first on scope dispose,
    // preventing onClose from firing and clearing our timers before VueUse's close() runs.
    if (getCurrentScope()) {
        onScopeDispose(() => {
            explicitlyDisconnected = true;
            clearConnectTimeout();
            clearDeadConnectionTimer();
            clearDisconnectedTimer();
        });
    }
    const { status, send: vueSend, open, close, ws } = useVueUseWebSocket(activeUrl, {
        immediate: false,
        autoConnect: false,
        autoReconnect: {
            retries: () => true,
            delay: (retries) => {
                const base = Math.min(RECONNECT_INITIAL_DELAY_MS * 2 ** retries, RECONNECT_MAX_DELAY_MS);
                return Math.max(0, Math.round(base + base * 0.5 * (2 * Math.random() - 1)));
            }
        },
        onConnected: () => {
            disconnectedSeconds.value = 0;
            disconnectedAt = null;
            clearDisconnectedTimer();
            resetDeadConnectionTimer();
            onOpen?.();
        },
        onDisconnected: (_ws, event) => {
            clearDeadConnectionTimer();
            if (!explicitlyDisconnected && disconnectedAt === null) {
                disconnectedAt = Date.now();
                disconnectedTimer = setInterval(() => {
                    disconnectedSeconds.value = Math.floor((Date.now() - disconnectedAt) / 1000);
                }, 1000);
                onClose?.(event?.code ?? 1000);
            }
        },
        onMessage: (sock, event) => {
            resetDeadConnectionTimer();
            let data;
            try {
                data = JSON.parse(event.data);
            }
            catch {
                // non-JSON message, fall through
            }
            if (data?.action === "ping") {
                sock.send(JSON.stringify({ action: "pong" }));
                return;
            }
            onMessage(event);
        }
    });
    const isConnected = computed(() => status.value === "OPEN");
    // Connect-timeout watchdog: if the socket stays CONNECTING beyond connectTimeoutMs,
    // force-close the raw socket so VueUse's autoReconnect takes over.
    // flush:'sync' ensures the timer starts/clears in the same tick as the status change.
    watch(status, (s) => {
        clearConnectTimeout();
        if (s === "CONNECTING") {
            connectTimeoutTimer = setTimeout(() => {
                connectTimeoutTimer = null;
                ws.value?.close();
            }, connectTimeoutMs);
        }
    }, { flush: "sync" });
    const connect = (url) => {
        explicitlyDisconnected = false;
        activeUrl.value = url;
        open();
    };
    const disconnect = () => {
        explicitlyDisconnected = true;
        clearConnectTimeout();
        clearDeadConnectionTimer();
        clearDisconnectedTimer();
        disconnectedSeconds.value = 0;
        close();
    };
    const send = (data) => {
        const serialized = typeof data === "string" ? data : JSON.stringify(data);
        vueSend(serialized, true);
    };
    return {
        isConnected: readonly(isConnected),
        disconnectedSeconds: readonly(disconnectedSeconds),
        connect,
        disconnect,
        send
    };
};
